/**
 * Shared non-JSX state, constants, types and utilities for the Upright chat module.
 *
 * Kept as a plain `.ts` file (no React imports) so every chat sub-module can
 * depend on it without pulling in the larger component tree. Module-level
 * state lives here rather than in React context because `makeAssistantToolUI`
 * may render tool UIs outside our context provider tree, and because
 * `uprightChatAdapter` is invoked by the assistant-ui runtime outside the
 * component tree.
 */
import type { ChatModelRunResult } from '@assistant-ui/react';
import { v4 as uuidV4 } from '@upright/common/utils/uuid';
import { CHAT_PROVIDERS, type ChatProvider, getCapabilities } from '@upright/common/chat/provider-capabilities';
import { loadChatDebugProvider, saveChatDebugProvider } from '../common/generic/utilities/storage';
import { ATTACHMENT_ACCEPT, logAttachmentUploadError, validateAttachmentFile } from './attachment-validation';
import { buildUploadErrorMessage } from './upload-error-message';
import { resetChatPanelDoubleClickGuard, type WebSearchHandOff } from './chat-panel-state';
import { resetRememberedSmallAttachments } from './reattach-attachments';
import { setActiveConversationId } from './history/chat-history-state';
import {
  getStickyPageChatBinding,
  resetStickyPageChatBinding,
  setStickyPageChatBinding,
} from './current-page-chat-binding';
import { resetStickyPageChatPathContext } from './current-page-chat-path-context';
import { isTrailOnlyResult } from './tool-trail-steps';

// ---------------------------------------------------------------------------
// Composer placeholder strings — single source of truth.
//
// Used both by our custom `UprightComposer` (which renders the actual textarea)
// and by the `Thread` component's `strings.composer.input.placeholder` config
// (which assistant-ui reads for the default composer path). Keeping them as
// a single constant prevents the two paths from silently drifting apart when
// copy is updated.
// ---------------------------------------------------------------------------
export const COMPOSER_PLACEHOLDER_DEFAULT = 'Ask about any company, fund, or portfolio…';
export const COMPOSER_PLACEHOLDER_REPLY = 'Reply…';
export function composerPlaceholder(hasMessages: boolean): string {
  return hasMessages ? COMPOSER_PLACEHOLDER_REPLY : COMPOSER_PLACEHOLDER_DEFAULT;
}

// ---------------------------------------------------------------------------
// Tool-result wrapper types.
//
// Tool results coming from the SSE stream are wrapped as `{ data, _ui?, ... }`.
// `ToolResultWithUi` is the common shape read by most tool UIs.
//
// `_partial: true` is a *structural* marker that says "this result object was
// constructed from a streaming-preview event, not from the server's final
// `tool-result`". It is set when the partial preview is built and is naturally
// absent on the final result (which fully replaces the object). It is NOT
// state to be cleared — whether the spinner should be visible is derived in
// the UI by combining this marker with the message-level `status.type ===
// "running"` from assistant-ui's runtime. When the run terminates for any
// reason (completion, abort, error), assistant-ui flips the status and the
// spinner stops by derivation, so there is no cleanup path to forget.
// ---------------------------------------------------------------------------
export interface ToolResultWithUi {
  data: unknown;
  _ui?: { appName?: string };
  _partial?: boolean;
  _bufferSize?: number;
}

// ---------------------------------------------------------------------------
// Chat logging / composer state.
//
// Shared between the Composer UI and the ChatModelAdapter. The adapter reads
// this when sending a message to include usage logging metadata (threadId,
// messageIndex, source). The web search choice has its own store below.
// ---------------------------------------------------------------------------
export const composerState = {
  threadId: uuidV4(),
  messageIndex: 0,
  /** Source of the next message: 'user_input', 'welcome_suggestion', 'followup_suggestion' or 'campaign_link' */
  pendingSource: 'user_input' as 'user_input' | 'welcome_suggestion' | 'followup_suggestion' | 'campaign_link',
};

/** Reset chat logging state for a new conversation. */
export function resetChatLoggingState(): void {
  composerState.threadId = uuidV4();
  composerState.messageIndex = 0;
  composerState.pendingSource = 'user_input';
  // No conversation is on screen until the new thread's first turn starts, which marks it
  // (`useChatConversationListSync`). If the id stayed set, the history list would highlight the
  // conversation the user has just left, and the live reconcile in `upright-chat.tsx`, which
  // fetches that id, would import its messages into the new thread on the next reconnect
  // (PLT-7749). Opening a stored conversation sets the id again after this reset.
  setActiveConversationId(null);
  // Clear any pending hidden entity context — `New chat` always starts a fresh
  // conversation with no prior entity binding. See `hiddenEntityContext` below.
  hiddenEntityContext.value = null;
  // Same for the visible page-context chip — a brand-new conversation has
  // no implicit page-of-origin to advertise. Goes through the setter so
  // subscribed components (the chip itself) re-render and unmount.
  setPageChatContext(null);
  // Drop any accumulated debug data — `New chat` should start the panel's
  // session totals from zero. See `chatDebugStore` below.
  resetChatDebugStore();
  // A click on the bar the user came from, right after this reset, is a
  // deliberate open rather than a double-click.
  resetChatPanelDoubleClickGuard();
  // Drop the sticky chat-context card mirrors. The sticky slots persist
  // across publisher unmount (e.g. expanding the side panel to full
  // `/chat`) so the chat-context chip survives mid-conversation; `New
  // chat` is the explicit "I'm done with that entity" signal that should
  // also clear the chip back to "no entity bound".
  resetStickyPageChatBinding();
  resetStickyPageChatPathContext();
  // Drop any remembered auto-re-attach file — `New chat` starts with no upload.
  resetRememberedSmallAttachments();
  // EU chats start with web search off (PLT-7271). Every way a chat starts —
  // `New chat`, opening a past chat, a seeded hand-off — goes through here.
  resetWebSearchChoiceForNewChat();
}

/**
 * `New chat` reset that keeps the client-company scoping the user is currently
 * working inside — but ONLY for DMA consultants.
 *
 * Plain `resetChatLoggingState` clears every company carrier (it nulls
 * `hiddenEntityContext` and the sticky binding). For a DMA consultant whose
 * chat is company-scoped that drops the `companyId` the next turn must send,
 * and the backend off-company guard then rejects the turn. So for consultants
 * we re-seed the carriers from the binding that was active before the reset,
 * and — on the full-screen `/chat` route — write the company back into the URL
 * so it also survives a later hard refresh (the URL is the durable carrier;
 * `chat-view` keeps these params for that reason).
 *
 * For everyone else this is a plain `resetChatLoggingState`: a non-consultant's
 * chat is not company-scoped, so carrying a page-of-origin company across an
 * explicit `New chat` could silently steer the fresh conversation toward the
 * wrong company. The `×` chip dismissal (`dismissPageContext`) remains the way
 * to drop the company entirely. The durable path-scoped route is the
 * post-launch PLT-5244.
 */
export function resetChatLoggingStateKeepingCompany(isConsultant: boolean): void {
  if (!isConsultant) {
    resetChatLoggingState();
    return;
  }
  const carriedBinding = getStickyPageChatBinding();
  const carriedHidden = hiddenEntityContext.value;
  resetChatLoggingState();
  if (carriedHidden !== null) {
    hiddenEntityContext.value = carriedHidden;
  }
  if (carriedBinding !== null) {
    setStickyPageChatBinding(carriedBinding);
  }
  // Persist into the URL only on the full `/chat` route — writing entity params
  // onto an entity page URL (e.g. `/company/:id/...`, where the side panel
  // lives) would be wrong, and there the page republishes the binding anyway.
  if (typeof window === 'undefined' || window.location.pathname !== '/chat') {
    return;
  }
  const entityId = carriedBinding?.entityId ?? carriedHidden?.entityId;
  const entityType = carriedBinding?.entityType ?? carriedHidden?.entityType;
  if (entityId === undefined || entityType === undefined) {
    return;
  }
  const url = new URL(window.location.href);
  url.searchParams.set('entityId', entityId);
  url.searchParams.set('entityType', entityType);
  if (carriedBinding?.label !== undefined && carriedBinding.label !== '') {
    url.searchParams.set('entityLabel', carriedBinding.label);
  }
  window.history.replaceState({}, '', url.toString());
}

/**
 * Reset the chat-logging state for a fresh conversation explicitly bound to a
 * NEW company — used when the user opens `/chat` for a different client company
 * than the one the currently-loaded conversation is about, so the new company
 * gets its own thread instead of inheriting the previous company's messages.
 *
 * Unlike `resetChatLoggingStateKeepingCompany` (which carries the *current*
 * company forward and is gated to consultants), this binds to the company the
 * caller passes in and applies to every user: the user explicitly navigated to
 * that company's chat, so the new binding is the intended one regardless of org
 * type. The caller is responsible for clearing the runtime thread itself
 * (`runtime.switchToNewThread()`) — this helper only owns the module-level
 * logging/binding state. The company already lives in the `/chat` URL (the
 * entry point put it there and `chat-view` keeps it), so no URL write is needed.
 */
export function resetChatLoggingStateForNewCompany(entityId: string, entityType: string, label?: string): void {
  // Clears all carriers (including the binding the page may have just published)
  // and rotates the threadId so the next turn is logged against a fresh thread.
  resetChatLoggingState();
  hiddenEntityContext.value = { entityId, entityType };
  if (label !== undefined && label !== '') {
    setStickyPageChatBinding({ entityId, entityType, label });
  }
}

// ---------------------------------------------------------------------------
// Selected chat provider store (PLT-5034 / PLT-5035).
//
// The staff EU testing switch is persisted in localStorage (team-only, in the
// debug panel), but the composer needs to react the instant a team user flips it so
// it can show / hide the affordances the provider can't serve (web search,
// file upload — both `false` on the EU Bedrock path). localStorage `storage`
// events only fire cross-tab, so we mirror the value in a tiny pub-sub here:
// the debug panel writes through `setSelectedChatProvider` (which persists AND
// notifies), and both the composer and the panel subscribe via
// `useSyncExternalStore`. The chat adapter keeps reading
// `loadChatDebugProvider()` straight from localStorage on each send — still
// correct, because the setter persists there too.
// ---------------------------------------------------------------------------
let _selectedChatProvider: ChatProvider = loadChatDebugProvider();
const _selectedChatProviderListeners = new Set<() => void>();

export function getSelectedChatProvider(): ChatProvider {
  return _selectedChatProvider;
}

export function setSelectedChatProvider(value: ChatProvider): void {
  // Persist on every call — including a no-op re-selection of the current
  // value — so the chat adapter's per-send `loadChatDebugProvider()` read can
  // never disagree with the store.
  saveChatDebugProvider(value);
  if (_selectedChatProvider === value) return;
  _selectedChatProvider = value;
  for (const l of _selectedChatProviderListeners) l();
}

export function subscribeSelectedChatProvider(listener: () => void): () => void {
  _selectedChatProviderListeners.add(listener);
  return () => {
    _selectedChatProviderListeners.delete(listener);
  };
}

// ---------------------------------------------------------------------------
// Active chat provider (PLT-7271).
//
// The provider this user's chat runs on, as `useActiveChatProvider` last
// resolved it. `null` until the first resolution: for a customer that is when
// their EU residency privilege has loaded. The adapter waits for it before
// deciding whether a message asks for web search, because a chat opened from a
// link (`/chat?prompt=…`) sends its first message straight after the page loads,
// before that privilege has arrived.
// ---------------------------------------------------------------------------
let _activeChatProvider: ChatProvider | null = null;
const _activeChatProviderWaiters = new Set<(provider: ChatProvider) => void>();

export function publishActiveChatProvider(provider: ChatProvider): void {
  _activeChatProvider = provider;
  for (const resolve of _activeChatProviderWaiters) resolve(provider);
  _activeChatProviderWaiters.clear();
}

/** The active provider, or `null` if it is still not known after `timeoutMs`. */
async function waitForActiveChatProvider(timeoutMs: number): Promise<ChatProvider | null> {
  if (_activeChatProvider !== null) return _activeChatProvider;
  return await new Promise<ChatProvider | null>(resolve => {
    const onKnown = (provider: ChatProvider) => {
      clearTimeout(timer);
      resolve(provider);
    };
    const timer = setTimeout(() => {
      _activeChatProviderWaiters.delete(onKnown);
      resolve(null);
    }, timeoutMs);
    _activeChatProviderWaiters.add(onKnown);
  });
}

// ---------------------------------------------------------------------------
// Web search choice (PLT-4605, PLT-7271).
//
// Kept per provider, because the two start differently:
//
//   - Default provider: on until the user turns it off (PLT-4605: the chat is a
//     research tool). The choice lasts until the page is reloaded.
//   - EU provider: off at the start of every chat, and the user turns it on for
//     the chat they are in. Web search on that path sends search terms to
//     ScrapingBee, which queries Google, both outside the EU. The model writes
//     the terms from the conversation, so they can carry customer context.
//
// A store rather than component state, so every surface with a toggle shows
// the same value and the adapter reads it at send time. `resetChatLoggingState`
// turns the EU choice back off; a launcher's own choice is applied after that
// reset by the hand-off (`useLauncherWebSearchToggle`). Which providers start off is the shared
// `webSearchOffAtChatStart` capability, which the backend also enforces: the
// request names the provider the choice was made for (`webSearchProvider`), so
// a wrong guess here about the user's provider cannot turn on EU web search.
// ---------------------------------------------------------------------------

/**
 * How long a send waits for the active provider before going without web
 * search. The privilege lookup behind it is one small request, so this only
 * runs out when that request has failed.
 */
const ACTIVE_PROVIDER_WAIT_MS = 5000;

export function webSearchOffAtChatStart(provider: ChatProvider): boolean {
  return getCapabilities(provider).webSearchOffAtChatStart;
}

let _webSearchChoice: Readonly<Record<ChatProvider, boolean>> = {
  anthropic: !webSearchOffAtChatStart('anthropic'),
  'bedrock-eu': !webSearchOffAtChatStart('bedrock-eu'),
};
const _webSearchChoiceListeners = new Set<() => void>();

export function getWebSearchChoice(): Readonly<Record<ChatProvider, boolean>> {
  return _webSearchChoice;
}

export function setWebSearchChoice(provider: ChatProvider, enabled: boolean): void {
  if (_webSearchChoice[provider] === enabled) return;
  _webSearchChoice = { ..._webSearchChoice, [provider]: enabled };
  for (const l of _webSearchChoiceListeners) l();
}

export function subscribeWebSearchChoice(listener: () => void): () => void {
  _webSearchChoiceListeners.add(listener);
  return () => {
    _webSearchChoiceListeners.delete(listener);
  };
}

function resetWebSearchChoiceForNewChat(): void {
  for (const provider of CHAT_PROVIDERS) {
    if (webSearchOffAtChatStart(provider)) setWebSearchChoice(provider, false);
  }
}

/**
 * The web search fields for the next request: `webSearchEnabled` and the
 * provider the user's choice was made for, when web search is on for the
 * active provider and that provider has it at all. Empty otherwise, including
 * when the provider is still unknown after `ACTIVE_PROVIDER_WAIT_MS`.
 */
export async function webSearchFieldsForNextRequest(): Promise<
  { webSearchEnabled: true; webSearchProvider: ChatProvider } | Record<string, never>
> {
  const provider = await waitForActiveChatProvider(ACTIVE_PROVIDER_WAIT_MS);
  if (provider === null) return {};
  if (getCapabilities(provider).webSearch === false || !_webSearchChoice[provider]) return {};
  return { webSearchEnabled: true, webSearchProvider: provider };
}

// ---------------------------------------------------------------------------
// Hidden entity context (PLT-4494, extended in PLT-4523, source migrated in
// PLT-4676).
//
// When the chat is entered via a deep-link that carries `?entityId=...` and
// `?entityType=...` query params, or seeded in-page through
// `openChatPanel({ entityId, entityType, extra })`, we want the model to
// know which entity the user's first question is about, plus any row-level
// identifiers needed to pin the answer to a specific data row — without
// showing the raw values in the user's own message bubble.
//
// The chat view (`chat-view/index.tsx`) captures `entityId`/`entityType`
// from the URL on mount and writes this module-level value before
// `InitialPromptAutoSender` appends the visible prompt. The side panel's
// seed-consumption effect (`chat-side-panel.tsx`) writes the same value
// when an `openChatPanel(...)` seed arrives — including any `extra` payload
// the caller passed in. `uprightChatAdapter` reads it on every send and
// prepends a hidden context line to the FIRST user message in the messages
// array before serializing to `/api/chat`. The runtime's stored messages
// are NOT mutated, so the user bubble keeps showing only the visible
// prompt. The value persists for the life of the conversation so follow-up
// turns also include the context (the backend re-receives the full message
// history on every turn). It is cleared on `New chat` or on a fresh `/chat`
// mount with no entity params, so a stale binding from a previous
// deep-link doesn't leak into a new conversation.
//
// `extra` (added in PLT-4523, source migrated to `openChatPanel` in
// PLT-4676) carries free-form `key=value` pairs (e.g. matterId, valence,
// driver, intent) that the panel opener wants the model to see. Entity
// binding stays in its own `entityId`/`entityType` slots so existing call
// sites that don't carry extras don't have to change.
// ---------------------------------------------------------------------------

export interface HiddenEntityContext {
  entityId: string;
  entityType: string;
  /** Optional free-form key/value pairs merged into the same context line. */
  extra?: Record<string, string>;
}

export const hiddenEntityContext: { value: HiddenEntityContext | null } = {
  value: null,
};

// ---------------------------------------------------------------------------
// Page context chip (PLT-4625 follow-up).
//
// When the chat is opened from a specific page in the platform (e.g. clicking
// a Net-Impact dimension bar on a company profile), we want to:
//
//   1) Pass the page URL into the model as additional hidden context so the
//      model knows what the user is currently looking at — `entityId` /
//      `entityType` already cover the model entity binding, but the URL gives
//      a richer hint about *what view* the user was on (net-impact lens,
//      DMA dashboard, financial effects, etc.).
//   2) Show a small visible chip above the thread with the page label + a
//      tiny Upright logo, so the user can see at a glance what context
//      Upbot has — similar in spirit to the citation chips, but for the
//      "what the user is asking about" side of the conversation.
//
// Mirrors the `hiddenEntityContext` mechanism: module-level value, set by the
// caller before `runtime.thread.append(prompt)`, read by the chat adapter on
// every send (so follow-ups carry the same context), cleared on `New chat`.
// ---------------------------------------------------------------------------

export interface PageChatContext {
  /** Short, human-readable label for the visible chip (e.g. "Neste — Net impact"). */
  label: string;
  /** Page URL the user was on when they opened the chat. Used both in the
   *  visible chip's `href` (chip is a link back to the page) and prepended
   *  to the model's hidden context line. */
  href: string;
  /**
   * Model entity type (`'company'` / `'group'` / `'portfolio'`) of the page
   * the chat was opened from. Free-form string to match `hiddenEntityContext`
   * / `CurrentPageChatBinding` (narrowed by consumers). Populated centrally in
   * the side-panel seed-consumption effect from the seed's `entityType`.
   *
   * The welcome screen uses this as the entity-type source for its suggestion
   * chips when no sticky binding is published — e.g. the dimension-bar seed
   * sets `pageChatContext` but no entity view mounts to publish a binding.
   * Without it the headline would name the company while the chips fell back
   * to the generic "explore" set (PLT-4938).
   */
  entityType?: string;
  /**
   * Lens label of the originating page (`'Net impact'`, `'Double materiality'`,
   * …), matching `lensLabelForImpactLens`. Set by the page-context producers
   * that know their lens (e.g. the net-impact dimension-bar / impact-bar
   * seeds). Lets the welcome chips stay lens-specific even when no sub-route
   * path-context publisher is mounted (PLT-4938 lens axis).
   */
  lensLabel?: string;
}

/**
 * Module-level store for the page-context chip. Backed by a tiny pub-sub so
 * the chip component re-renders whenever the value changes (set by the
 * panel's seed-consumption effect, cleared by `resetChatLoggingState`).
 *
 * Mutation goes through `setPageChatContext` rather than direct field
 * assignment so subscribers are always notified — accidentally writing
 * `pageChatContext.value = …` would silently leave the chip stale, so
 * the field is left readable but encouraged-private (consumers read
 * `getPageChatContext()` and only `setPageChatContext` mutates).
 */
const _pageChatContext: { value: PageChatContext | null } = { value: null };
const _pageChatContextListeners = new Set<() => void>();

export const pageChatContext: {
  /** Direct read of the current value — for non-React callers (chat
   *  adapter, dimension bar, etc.). Reactive consumers should use
   *  `subscribePageChatContext` + `getPageChatContext` instead. */
  readonly value: PageChatContext | null;
} = _pageChatContext;

export function setPageChatContext(value: PageChatContext | null): void {
  if (_pageChatContext.value === value) return;
  _pageChatContext.value = value;
  for (const l of _pageChatContextListeners) l();
}

export function getPageChatContext(): PageChatContext | null {
  return _pageChatContext.value;
}

export function subscribePageChatContext(listener: () => void): () => void {
  _pageChatContextListeners.add(listener);
  return () => {
    _pageChatContextListeners.delete(listener);
  };
}

// ---------------------------------------------------------------------------
// Deep-link seeding flag (PLT-6108).
//
// `InitialPromptAutoSender` (`upright-chat.tsx`) seeds a `?prompt=` deep link by
// awaiting `runtime.threads.switchToNewThread()`, then attaching any hand-off
// files, then appending. Across those awaits the thread is empty and idle, so
// the welcome screen renders its suggestion pills and they look live — but a
// pill clicked there appends to the thread that is about to be switched away
// from, and the deep-linked prompt then runs alongside it under a different
// thread id. Thread state cannot express this window: `isRunning` is false
// precisely because no message exists yet, which is also why the pills are on
// screen at all. So the auto-sender publishes the window and the pills read it.
//
// Not covered: the frame between the pills first painting and the auto-sender's
// effect running. Nobody can click inside it, and the file-attachment path — the
// one that keeps this window open for whole seconds — is well past it.
// ---------------------------------------------------------------------------
let _seedingInitialPrompt = false;
const _seedingInitialPromptListeners = new Set<() => void>();

export function setSeedingInitialPrompt(value: boolean): void {
  if (_seedingInitialPrompt === value) return;
  _seedingInitialPrompt = value;
  for (const l of _seedingInitialPromptListeners) l();
}

export function getSeedingInitialPrompt(): boolean {
  return _seedingInitialPrompt;
}

export function subscribeSeedingInitialPrompt(listener: () => void): () => void {
  _seedingInitialPromptListeners.add(listener);
  return () => {
    _seedingInitialPromptListeners.delete(listener);
  };
}

// ---------------------------------------------------------------------------
// Composer focus requests (PLT-6766)
//
// "Start a new chat" ends with the cursor in the composer. The composer already
// autofocuses on mount, which covers a user arriving at `/chat` from elsewhere
// — but inside the unified shell the chat is a view of a shell that is already
// up, so going from an open conversation to a new one remounts nothing and the
// focus would stay wherever the click left it.
//
// A counter rather than a flag: two new chats in a row are two requests, and a
// boolean would have to be cleared by the consumer to fire a second time. Every
// mounted composer answers, which is what we want — the only case where two are
// mounted at once is the side panel over `/chat`, and `startNewChat` closes the
// panel in the same handler, so its composer is gone by the time the effect
// runs.
// ---------------------------------------------------------------------------
let _composerFocusRequests = 0;
const _composerFocusListeners = new Set<() => void>();

export function requestComposerFocus(): void {
  _composerFocusRequests += 1;
  for (const l of _composerFocusListeners) l();
}

export function getComposerFocusRequests(): number {
  return _composerFocusRequests;
}

export function subscribeComposerFocusRequests(listener: () => void): () => void {
  _composerFocusListeners.add(listener);
  return () => {
    _composerFocusListeners.delete(listener);
  };
}

// ---------------------------------------------------------------------------
// Chat debug store — feeds the internal-mode debug panel (PLT-4475).
//
// Module-level (not Redux) by design: the data is per-thread, large-ish, and
// only consumed by one component (the debug panel). Putting it in Redux would
// thrash every chat re-render even when internal mode is off. Instead we keep
// a tiny pub-sub here and the panel subscribes via `useSyncExternalStore`.
//
// The data lifecycle mirrors the message lifecycle: each `usage` SSE event
// from the chat backend appends one entry, indexed by messageIndex, and
// `resetChatLoggingState` clears it on `New chat`.
// ---------------------------------------------------------------------------

/**
 * One LLM round's timing + token breakdown (PLT-5819), mirroring the backend
 * `ChatRoundProfile` (chat-round-profiler.ts). Kept as a frontend-local copy
 * because the platform build can't import from `src/services/main`; the shape
 * is a plain data contract carried on the `usage` SSE event, so a drift here
 * only affects what the debug panel renders — never correctness of a request.
 * All `*Ms` values are wall-clock; `null` where the stream produced no such
 * signal (see the backend JSDoc for the thinking-vs-generation caveat).
 */
export interface ChatRoundProfile {
  /** 0-based round index within the turn. */
  round: number;
  model: string;
  effort: string | null;
  reasoningEnabled: boolean;
  /** Model prefill + thinking latency: request → SDK's first event of any kind. */
  timeToFirstEventMs: number | null;
  /** Request → first user-visible content (text or thinking summary). */
  timeToFirstContentMs: number | null;
  /** Full model phase: stream consumption + finalMessage() + intra-round retries. */
  modelMs: number;
  /** Wall-clock executing this round's tools (0 on the final answer round). */
  toolMs: number;
  inputTokens: number;
  /** Output tokens — generation AND thinking combined (see backend doc). */
  outputTokens: number;
  cacheReadTokens: number;
  cacheCreationTokens: number;
  /** Tool names the model called this round, in order. Empty on the answer round. */
  tools: string[];
  stopReason: string | null;
}

/** Per-message usage event payload received from the backend. */
export interface ChatDebugMessageUsage {
  /** 0-based message index inside the current thread. */
  messageIndex: number;
  /** Anthropic model id used for this turn (e.g. `claude-opus-5`). */
  model: string;
  inputTokens: number;
  outputTokens: number;
  cacheCreationTokens: number;
  cacheReadTokens: number;
  /** Wall-clock ms from request start to final SSE event. */
  durationMs: number;
  /** `null` when the stream was aborted before stop_reason landed. */
  stopReason: string | null;
  timeBreakdown: {
    modelMs: number;
    toolMs: number;
    otherMs: number;
  };
  toolsUsed: Array<{
    toolCallId: string;
    name: string;
    durationMs: number;
    /** Approximation — see backend index.ts for why. */
    approxResultTokens: number;
    failed: boolean;
    /**
     * Raw backend error message — populated only for failed tools and only
     * forwarded by the server inside the `usage` SSE event. The chat UI
     * scrubs the model-facing tool-result for security (so the LLM does not
     * narrate failures as architecture), but team users browsing in
     * internal mode still need to see what actually went wrong. The debug
     * panel renders this string verbatim under the failed tool row; the
     * panel itself is gated by `useIsChatInternalMode`, so the field never
     * surfaces to non-team users. PLT-4620 follow-up.
     */
    errorMessage?: string;
  }>;
  /** USD cost estimate. `null` when the model isn't in the rate table. */
  costUsd: number | null;
  costBreakdownUsd: {
    input: number;
    output: number;
    cacheWrite: number;
    cacheRead: number;
  } | null;
  /**
   * Per-LLM-round profiling (PLT-5819). Present only when profiling is enabled
   * for the turn (team user + chat internal mode, or the `CHAT_ROUND_PROFILE`
   * env flag). `undefined` otherwise, so the panel's round breakdown stays
   * hidden on ordinary turns. Ordered by `round` as the backend recorded them.
   */
  roundProfiles?: ChatRoundProfile[];
  /** Wall-clock at which the panel received this entry. */
  receivedAt: number;
}

interface ChatDebugStoreSnapshot {
  /** Most recent first — index 0 is the latest message. */
  entries: ChatDebugMessageUsage[];
}

let _debugSnapshot: ChatDebugStoreSnapshot = { entries: [] };
const _debugListeners = new Set<() => void>();

/**
 * Returns the current snapshot. Reference-stable between mutations so
 * `useSyncExternalStore` re-renders only when something actually changed.
 */
export function getChatDebugSnapshot(): ChatDebugStoreSnapshot {
  return _debugSnapshot;
}

/** Subscribe to debug-store mutations. Returns an unsubscribe function. */
export function subscribeChatDebugStore(listener: () => void): () => void {
  _debugListeners.add(listener);
  return () => {
    _debugListeners.delete(listener);
  };
}

/**
 * Append a usage entry. Called from the chat adapter when a `usage` SSE event
 * lands. Replaces any prior entry for the same `messageIndex` (which can
 * happen on a regenerate) so the panel always shows the latest measurement.
 */
export function appendChatDebugEntry(entry: ChatDebugMessageUsage): void {
  const next = _debugSnapshot.entries.filter(e => e.messageIndex !== entry.messageIndex);
  next.unshift(entry);
  _debugSnapshot = { entries: next };
  for (const l of _debugListeners) l();
}

/** Reset the store. Called from `resetChatLoggingState` on `New chat`. */
export function resetChatDebugStore(): void {
  if (_debugSnapshot.entries.length === 0) return;
  _debugSnapshot = { entries: [] };
  for (const l of _debugListeners) l();
}

// ---------------------------------------------------------------------------
// Pending hand-off attachments (PLT-5051).
//
// Files picked on a *runtime-less* launcher — the launcher composer
// (`LandingChatEntry`) — that should ride along with the first message
// once the user is handed off to full `/chat`. The landing has no chat
// runtime mounted (there is one global runtime, consumed by `/chat` + the
// side panel), so it can't add attachments through assistant-ui directly.
// Instead it stashes the raw `File` objects here and `InitialPromptAutoSender`
// (in `upright-chat.tsx`) consumes them on mount: each file is run through
// `uprightAttachmentAdapter` to produce a base64 attachment, appended with
// the auto-sent prompt, and the slot is cleared. Plain module-level state
// (like `composerState`) so it survives the in-app navigation to `/chat`.
// ---------------------------------------------------------------------------
export const pendingChatAttachments: { value: File[] } = { value: [] };

/**
 * The launcher composer's web search choice, handed to `/chat` the same way as
 * the files above and applied by `InitialPromptAutoSender` after the new chat's
 * reset (PLT-7271). Set only on a provider whose web search is off at the start
 * of each chat; see `useLauncherWebSearchToggle`. Not a URL parameter, so a
 * shared `/chat?prompt=` link cannot turn on web search for an EU user.
 */
export const pendingWebSearchHandOff: { value: WebSearchHandOff | null } = { value: null };

// ---------------------------------------------------------------------------
// Attachment adapter — reads files in-browser as base64 (no S3 upload).
//
// The accepted types and the size ceiling live in `attachment-validation.ts`
// (PLT-6061), which is also what every call site checks a file against *before*
// handing it to `addAttachment`, so a refusal can be shown to the user rather
// than surfacing as a thrown Error nobody catches.
// ---------------------------------------------------------------------------

/** Read a File as a base64-encoded string. */
async function readFileAsBase64(file: File): Promise<string> {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // result is "data:<mime>;base64,<data>" — strip the prefix
      const result = reader.result as string;
      resolve(result.split(',')[1]);
    };
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Attachment adapter that reads files in-browser as base64.
 * No S3 upload needed — base64 data is sent inline with the chat message.
 */
export const uprightAttachmentAdapter = {
  accept: ATTACHMENT_ACCEPT,

  async add({ file }: { file: File }) {
    // Last gate before we spend memory on base64. Call sites validate first and
    // report the refusal in the UI, so reaching this throw means a path that
    // skipped that check (or `add()` being called directly, as the landing
    // hand-off in `upright-chat.tsx` does) — hence the thrown message is the
    // same user-facing sentence, not a developer string. PLT-6061.
    const rejection = validateAttachmentFile(file);
    if (rejection !== undefined) {
      // Reaching this gate means a call site skipped its own check, so this is
      // the one refusal path with no other logging behind it — and the raw MIME
      // type is no longer in the thrown (user-facing) sentence. PLT-6061.
      logAttachmentUploadError('attachment-adapter', rejection);
      throw new Error(buildUploadErrorMessage(rejection));
    }

    // Read the file as base64 in the browser
    const base64 = await readFileAsBase64(file);

    return {
      id: `local-${Date.now()}-${file.name}`,
      type: file.type.startsWith('image/') ? ('image' as const) : ('document' as const),
      name: file.name,
      contentType: file.type,
      file,
      // Store base64 data so we can send it with the chat message
      content: [
        {
          type: 'file' as const,
          data: base64,
          mimeType: file.type || 'application/octet-stream',
          filename: file.name,
        },
      ],
      status: { type: 'requires-action' as const, reason: 'composer-send' as const },
    };
  },

  send(attachment: any) {
    return {
      ...attachment,
      status: { type: 'complete' as const },
    };
  },

  async remove() {
    // No-op — file data is in memory only
  },
};

// ---------------------------------------------------------------------------
// Error content — used by the adapter to surface errors as styled cards.
// ---------------------------------------------------------------------------

/** Returns content for a synthetic error tool-call that renders as a styled error card. */
export function makeErrorContent(message: string): ChatModelRunResult['content'] {
  return [
    {
      type: 'tool-call' as const,
      toolCallId: `err-${Date.now()}`,
      toolName: 'chat_error',
      argsText: JSON.stringify({ message }),
      args: { message },
      result: {},
    },
  ];
}

/**
 * PLT-5003: content for a synthetic `ai_chat_token_limit` tool-call. Rendered
 * by `ChatTokenLimitCard` (a friendly "you've reached your usage limit" card)
 * instead of the generic red error block, when the backend returns 429 with
 * `errorCode: AI_CHAT_TOKEN_LIMIT_REACHED`. The card resolves its tier-specific
 * CTA from frontend signals.
 *
 * PLT-5266: `companyId` is the client company the turn was metered against (the
 * adapter's `boundCompanyId`). The card uses it to offer a consultant admin a
 * self-serve Starter→Pro upgrade for that company; it is absent for org-level /
 * non-company-scoped chat, where the card keeps the generic CTA.
 */
export function makeTokenLimitContent(companyId?: string | null): ChatModelRunResult['content'] {
  // Typed as a concrete string-valued record so the empty-object branch widens
  // to the same type; an inferred `{ companyId?: undefined }` union arm is not
  // assignable to the tool-call `args` JSON-object type.
  const args: Record<string, string> = companyId ? { companyId } : {};
  return [
    {
      type: 'tool-call' as const,
      toolCallId: `token-limit-${Date.now()}`,
      toolName: 'ai_chat_token_limit',
      argsText: JSON.stringify(args),
      args,
      result: {},
    },
  ];
}

// ---------------------------------------------------------------------------
// Payload size reduction — stops the browser from echoing large tool data
// (e.g. base64 image data URLs from code_execution) back to the server on
// follow-up messages.
// ---------------------------------------------------------------------------

/**
 * Strip large payload fields from message history before sending to the server.
 * These payloads are only needed for client-side rendering; sending them back
 * bloats the payload and can exceed the 1 MB Hapi limit on follow-up messages.
 */
export function stripLargeToolData(content: any[]): any[] {
  return content
    .filter((part: any) => {
      // Remove suggest_followups entirely — it's UI-only and adds no context for follow-ups
      if (part.type === 'tool-call' && part.toolName === 'suggest_followups') return false;
      // Reasoning summaries are per-message ephemeral UI affordances
      // synthesised client-side from `reasoning-*` SSE events. They have
      // no meaning to the server (which doesn't define a
      // `reasoning_summary` tool), so dropping them prevents the
      // adapter from echoing a tool-call the server can't deserialize.
      // The actual `thinking` content blocks travel back to Anthropic
      // via `finalMessage.content` on the server side, not through this
      // payload.
      if (part.type === 'tool-call' && part.toolName === 'reasoning_summary') return false;
      // PLT-7912: a reopened chat's tool-trail rows carry a placeholder result, not what the tool
      // returned, so the model would read them as calls that came back empty. A reopened chat sent
      // only its text and cards before the trail was persisted, and still does.
      if (part.type === 'tool-call' && isTrailOnlyResult(part.result)) return false;
      return true;
    })
    .map((part: any) => {
      if (part.type !== 'tool-call') return part;

      const stripped = { ...part };
      // PLT-5030: drop the base64 image data URLs from a code_execution result
      // before echoing history back to the server. The model already saw the
      // generated file natively (its `file_id` rides back in `finalMessage`
      // content server-side), so resending megabytes of base64 here would just
      // bloat the request and burn tokens.
      if (part.toolName === 'code_execution' && Array.isArray(part.result?.data?.images)) {
        const restData = { ...part.result.data };
        delete restData.images;
        stripped.result = { ...part.result, data: restData };
      }
      // PLT-5520: the fund-review card tools return the full per-holding `rows`
      // array (and the rendered `issuesTable`) only so the client can draw the
      // card. The model saw a compact summary at creation time and the server
      // re-strips these on replay anyway, so echoing them back is pure bloat —
      // ~500 KB for a 1k-holding fund, enough to breach the 1 MB Hapi limit
      // once two such cards are in history. Drop them here so they never leave
      // the browser. The client's own runtime keeps the full rows for
      // rendering; only the outgoing payload is trimmed.
      if (
        (part.toolName === 'review_staged_fund' ||
          part.toolName === 'create_fund_from_staged' ||
          part.toolName === 'review_fund_version') &&
        part.result?.data &&
        typeof part.result.data === 'object'
      ) {
        const restData = { ...part.result.data };
        delete restData.rows;
        delete restData.issuesTable;
        stripped.result = { ...part.result, data: restData };
      }
      return stripped;
    });
}
